import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useResizableWidth } from '../hooks/use-resizable-width';
import { ResizeHandle } from './ResizeHandle';

/**
 * Smallest the sibling detail/preview pane is allowed to get. The list pane's
 * effective max width is capped at (container width − this) so dragging the
 * list wide can never push the detail off-screen. The detail clips/scrolls its
 * own content below this, but it never disappears or bleeds past the frame.
 */
const DETAIL_MIN_WIDTH = 360;

interface Props {
  /**
   * Stable, unique persistence key for this pane's width (namespaced in
   * localStorage). e.g. 'conversations', 'skills', 'skill-files'.
   */
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /**
   * Style for the list column itself (border, background, overflow, etc.).
   * `width` is supplied by the hook and must NOT be set here — it's overridden.
   */
  paneStyle?: CSSProperties;
  children: ReactNode;
}

/**
 * Drops a draggable resize seam onto a fixed-width list column in a
 * list→preview layout. Renders `[ list column ][ resize handle ]` as flex
 * siblings, so the parent should be `display:flex` with the detail pane as a
 * `flex:1` sibling AFTER this component. The chosen width persists per-pane.
 *
 * The effective max width is the SMALLER of the configured `maxWidth` and
 * `(container width − DETAIL_MIN_WIDTH)`, so on a narrow window the list can't
 * be dragged wide enough to crowd the detail pane off-screen. If the window
 * shrinks below what the current width allows, the pane auto-shrinks to fit.
 *
 * This is the secondary-list counterpart to the shell's left-rail resize.
 */
export function ResizableListPane({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  paneStyle,
  children,
}: Props) {
  // Measure the parent flex row so the max can adapt to the available width.
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const pane = paneRef.current;
    const row = pane?.parentElement;
    if (!row) return;
    const measure = () => setContainerWidth(row.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    return () => ro.disconnect();
  }, []);

  // The hard ceiling: never let the list pane leave the detail less than
  // DETAIL_MIN_WIDTH. Falls back to the configured max until measured.
  const effectiveMax =
    containerWidth == null
      ? maxWidth
      : Math.max(minWidth, Math.min(maxWidth, containerWidth - DETAIL_MIN_WIDTH));

  const { width, dragging, onResize, onResizeEnd } = useResizableWidth(
    storageKey,
    defaultWidth,
    minWidth,
    effectiveMax,
  );

  // If the window shrank below what the persisted width allows, pull the pane
  // back in so the detail keeps its minimum. (Only down-clamp — never widen on
  // resize, so the user's chosen width is restored when the window grows back.)
  useEffect(() => {
    if (width > effectiveMax) onResize(effectiveMax);
  }, [effectiveMax, width, onResize]);

  return (
    <>
      <div
        ref={paneRef}
        style={{
          ...paneStyle,
          width: Math.min(width, effectiveMax),
          flexShrink: 0,
          transition: dragging ? 'none' : undefined,
        }}
      >
        {children}
      </div>
      <ResizeHandle
        width={Math.min(width, effectiveMax)}
        min={minWidth}
        max={effectiveMax}
        onResize={onResize}
        onResizeEnd={onResizeEnd}
      />
    </>
  );
}
